/* eslint-disable react/no-danger */

import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { t } from 'ttag';
import { Button } from '@components';

/**
 * `Modal` component provides a way to display content in a modal overlay. It supports custom content and an optional title.
 * It can be opened or closed using React state management.
 *
 * @component Modal
 * @param {Object} props - The properties for the Modal component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the modal.
 * @param {boolean} props.isOpen - State to control the visibility of the modal. Should be managed via useState hook or similar.
 * @param {Function} props.onClose - The function to call when the modal needs to be closed. Typically, this function will set the state managed by `isOpen` to false.
 * @param {string} props.title - Optional title for the modal.
 * @param {Object} [restProps] - Additional properties to be passed to the Modal component.
 * @returns {ReactElement} The rendered Modal component.
 * @example
 * import React, { useState } from 'react';
 * import { Modal } from '@/modules/Modal/Modal';
 *
 * const MyComponent = () => {
 *   const [isModalOpen, setIsModalOpen] = useState(false);
 *
 *   const handleOpenModal = () => {
 *     setIsModalOpen(true);
 *   };
 *
 *   const handleCloseModal = () => {
 *     setIsModalOpen(false);
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleOpenModal}>Open Modal</button>
 *       <Modal
 *         isOpen={isModalOpen}
 *         onClose={handleCloseModal}
 *         title="My Modal Title"
 *       >
 *         <p>This is the content of the modal.</p>
 *       </Modal>
 *     </div>
 *   );
 * };
 *
 * export default MyComponent;
 */

export default function Modal({
  children,
  isOpen,
  onClose,
  title,
  ...restProps
}) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        onClose={onClose}
        className="fixed inset-0 z-[8000] flex items-center justify-center bg-gray/25 p-4  backdrop-blur-sm md:pt-4"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="absolute inset-0"
            aria-hidden="true"
            onClick={onClose}
          />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-200"
          enterFrom="transform-gpu scale-95 opacity-0"
          enterTo="transform-gpu scale-100 opacity-100"
          leave="ease-in-out duration-200"
          leaveFrom="transform-gpu scale-100 opacity-100"
          leaveTo="transform-gpu scale-95 opacity-0"
        >
          <Dialog.Panel className="max-h-[calc(100%-16px)] w-full overflow-y-auto rounded-xl bg-white px-6 shadow-xl md:max-w-screen-sm md:px-8 lg:max-w-screen-md lg:px-12">
            <div className="sticky top-0 z-10 bg-white py-4 md:py-8 xl:py-12">
              <div className="flex justify-end">
                <Button
                  variant="tertiary"
                  onClick={onClose}
                  aria-label={t`Close Modal`}
                  className="flex w-fit cursor-pointer items-center justify-between gap-x-1 border-0 bg-transparent px-2 py-2 leading-none text-black no-underline outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50"
                  {...restProps}
                >
                  <Button.Icon className="flex h-6 w-6 items-center justify-center fill-black">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="h-full w-full"
                    >
                      <path d="M12.0007 10.5865L16.9504 5.63672L18.3646 7.05093L13.4149 12.0007L18.3646 16.9504L16.9504 18.3646L12.0007 13.4149L7.05093 18.3646L5.63672 16.9504L10.5865 12.0007L5.63672 7.05093L7.05093 5.63672L12.0007 10.5865Z" />
                    </svg>
                  </Button.Icon>
                </Button>
              </div>
            </div>
            <Dialog.Title
              as="div"
              className="mb-4 text-lg font-bold md:text-xl"
            >
              {title}
            </Dialog.Title>
            <div className="p-4">{children}</div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}
